// src/modules/lead/pages/ClientList.js
import React, { useEffect, useState } from "react"
import { Card, Badge, Dropdown } from "react-bootstrap"
import { Link } from "react-router-dom"
import {
  Icon,
  CustomDropdownToggle,
  CustomDropdownMenu,
  LinkList,
} from "../../../components"
import Layout from "../../../layout/default"
import Block from "../../../components/Block/Block"
import DataTable from "../../../components/DataTable/DataTable.js"
import leadService from "../services/leadService"
import mainSpecialtyService from "../../main_specialty/services/main_specialtyService"
import { COLOR_STATE_MAP } from "../../../constants/stateColors.js"

const ClientList = () => {
  /* ---------------- state ---------------- */
  const [leadData, setLead] = useState([])
  const [loading, setLoading] = useState(false)
  const [specialtyData, setSpecialty] = useState([])
  const [mainSpecialtyData, setMainSpecialty] = useState([])
  const [provinceData, setProvince] = useState([])

  /* ---------------- load ----------------- */
  useEffect(() => {
    fetchAll()
  }, [])

  const fetchAll = async () => {
    try {
      setLoading(true)
      const resp = await leadService.getLeadListPaginate(1, {}, 10)
      setLead(resp.results)

      const [spec, mainSpec, prov] = await Promise.all([
        leadService.getSpecialtyList(),
        mainSpecialtyService.getMain_specialtyList(),
        leadService.getProvinceList(),
      ])
      setSpecialty(spec)
      setMainSpecialty(mainSpec)
      setProvince(prov)
    } finally {
      setLoading(false)
    }
  }

  /* -------- filtramos sólo clientes ------------ */
  const assignedClients = leadData.filter(
    (l) => l.assignments && l.assignments.length > 0
  )

  /* -------- handler delete ------------ */
  const handleDeleteClient = async (clientId) => {
    try {
      setLoading(true)
      await leadService.deleteLead(clientId)
      setLead((prev) => prev.filter((c) => c.id !== clientId))
    } finally {
      setLoading(false)
    }
  }

  /* ---------------- columns ------------------ */
  const clientColumns = [
    { name: "ID", selector: (row) => row.id, sortable: true },
    {
      name: "Fecha",
      selector: (row) => new Date(row.assignation_date).toLocaleString("es-ES"),
      sortable: true,
    },
    { name: "Nombre", selector: (row) => row.name, sortable: true },
    { name: "Apellidos", selector: (row) => row.last_name, sortable: true },
    { name: "Origen", selector: (row) => row.origin, sortable: true },
    {
      name: "Beneficio esperado",
      selector: (row) =>
        row.lead_assignments?.length
          ? row.lead_assignments
              .map((a) => (a.estimated_value ? `${a.estimated_value} €` : "-"))
              .join(", ")
          : "-",
      sortable: true,
    },
    { name: "Email", selector: (row) => row.email, sortable: true },
    { name: "Teléfono", selector: (row) => row.phone_number, sortable: true },
    {
      name: "Especialidad",
      selector: (row) => {
        const s = specialtyData.find((sp) => sp.id === row.specialty)
        return s ? s.name : ""
      },
      sortable: true,
      cell: (row) => {
        const m = mainSpecialtyData.find((x) => x.id === row.main_specialty)
        return m ? m.name : ""
      },
    },
    {
      name: "Sub-especialidad",
      selector: (row) => {
        const s = specialtyData.find((sp) => sp.id === row.specialty)
        return s ? s.child_specialty : ""
      },
      sortable: true,
    },
    {
      name: "Provincia",
      selector: (row) => {
        const p = provinceData.find((pr) => pr.id === row.province)
        return p ? p.name : ""
      },
      sortable: true,
    },
    {
      name: "SMS verificado",
      selector: (row) =>
        row.sms_verified ? (
          <Badge bg="success">Yes</Badge>
        ) : (
          <Badge bg="warning">No</Badge>
        ),
      sortable: true,
    },
    {
      name: "Estado",
      minWidth: "300px",
      selector: (row) => row.state_assignment,
      cell: (row) =>
        row.state_assignment?.length ? (
          <span>
            {row.state_assignment.map((sa) => {
              const st = sa.state?.state || ""
              const sb = sa.substate?.substate || ""
              const bg = COLOR_STATE_MAP[st] || "#5f38f9"
              return (
                <Badge
                  key={sa.id}
                  style={{ backgroundColor: bg, marginRight: "5px" }}
                >
                  {st}
                  {sb ? ` | ${sb}` : ""}
                </Badge>
              )
            })}
          </span>
        ) : (
          <Badge style={{ backgroundColor: "#5f38f9", color: "white" }}>
            Sin estados
          </Badge>
        ),
      sortable: true,
    },
    {
      name: "Asignaciones",
      selector: (row) => (
        <div style={{ display: "flex", gap: "0.5rem" }}>
          {row.assignments.map((a) => (
            <Badge bg="info" key={a}>
              <Link to={`/lawyer/${a}`}>
                <span style={{ color: "white" }}>{a}</span>
              </Link>
            </Badge>
          ))}
        </div>
      ),
    },
    {
      name: "Acciones",
      cell: (row) => (
        <div className="text-end w-100">
          <Dropdown>
            <Dropdown.Toggle
              size="sm"
              as={CustomDropdownToggle}
              className="btn-icon me-n1"
            >
              <Icon name="more-v" />
            </Dropdown.Toggle>

            <Dropdown.Menu
              className="dropdown-menu-sm"
              as={CustomDropdownMenu}
              align="end"
              renderOnMount
              container={document.body}
              popperConfig={{ strategy: "fixed" }} 
              style={{ zIndex: 3000 }} 
            >
              <div className="dropdown-content py-1">
                <LinkList className="link-list-hover-bg-primary link-list-md">
                  <Link to={`/lead/edit/${row.id}`} className="dropdown-item">
                    <Icon name="edit" />
                    <span>Edit</span>
                  </Link>
                  <button
                    className="dropdown-item"
                    onClick={() => handleDeleteClient(row.id)}
                  >
                    <Icon name="trash" />
                    <span>Delete</span>
                  </button>
                </LinkList>
              </div>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      ),
      sortable: false,
    },
  ]

  /* ---------------- render ------------------ */
  return (
    <Layout title="Clientes" content="container" loading={loading}>
      <Block.Head>
        <Block.HeadBetween>
          <Block.HeadContent>
            <Block.Title tag="h2">Clientes</Block.Title>
            <nav>
              <ol className="breadcrumb breadcrumb-arrow mb-0">
                <li className="breadcrumb-item">
                  <Link to="/">Home</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Clientes
                </li>
              </ol>
            </nav>
          </Block.HeadContent>
        </Block.HeadBetween>
      </Block.Head>

      <Block>
        <Card>
          {!loading && (
            <DataTable
              tableClassName="data-table-head-light table-responsive"
              data={assignedClients}
              columns={clientColumns}
              searchByList={[
                "id",
                "name",
                "last_name",
                "email",
                "phone_number",
              ]}
              pagination
              paginationPerPage={10}
              paginationRowsPerPageOptions={[10, 25, 50, 100]}
              highlightOnHover
              dense
            />
          )}
        </Card>
      </Block>
    </Layout>
  )
}

export default ClientList
